import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormState } from "react-hook-form";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { RadioGroup, FormControlLabel, Radio, Box } from "@mui/material";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";

const PAGE_LOCALE_KEY = "page.merchants.";
const FORM_LABEL_KEY =
  PAGE_LOCALE_KEY + "merchantDetail.info.companyInfo.fields";

const AgentAssignmentSection = ({
  formProps,
  isLoading,
  agentList = [],
  onSearch,
  defaultAgent,
  hasMoreAgents = false,
}) => {
  const { t } = useTranslation();
  const { setValue, register, clearErrors, control, getValues } = formProps;
  const { errors } = useFormState({ control, name: "isoAgentEmail" });
  const isDisabled = isLoading;
  const agentError = errors?.isoAgentEmail;
  const currentAgentEmail = `${getValues("isoAgentEmail") || ""}`.trim();
  const currentAgentId = getValues("isoAgentId");

  const [mode, setMode] = useState(currentAgentEmail && !currentAgentId ? "new" : "existing");
  const [selectedAgent, setSelectedAgent] = useState(
    currentAgentEmail
      ? {
        key: currentAgentEmail,
        title: currentAgentId ? `${currentAgentId} - ${currentAgentEmail}` : currentAgentEmail,
        value: currentAgentEmail,
        id: currentAgentId,
        email: currentAgentEmail,
      }
      : defaultAgent || null,
  );

  useEffect(() => {
    register("isoAgentEmail", { required: "Mandatory" });
    register("isoAgentId");
  }, [register]);

  useEffect(() => {
    const applyDefaultAgent = () => {
      setSelectedAgent(defaultAgent);
      if (formProps) {
        const { setValue } = formProps;
        setValue("isoAgentEmail", defaultAgent.value, { shouldValidate: true });
        setValue("isoAgentId", defaultAgent.id, { shouldValidate: true });
        clearErrors("isoAgentEmail");
      }
    };

    if (defaultAgent?.email && !getValues("isoAgentEmail")) {
      applyDefaultAgent();
    }
  }, [clearErrors, defaultAgent?.email, formProps, getValues]);

  useEffect(() => {
    if (mode === "new") {
      setValue("isoAgentId", null, { shouldValidate: true });
    }
  }, [mode, setValue]);

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    setMode(newMode);
    setValue("isoAgentEmail", "", { shouldValidate: true });
    setValue("isoAgentId", null, { shouldValidate: true });
    setSelectedAgent(null);
  };

  const handleAgentChange = (event, newValue) => {
    setSelectedAgent(newValue);
    if (newValue) {
      setValue("isoAgentEmail", newValue.value, { shouldValidate: true });
      setValue("isoAgentId", newValue.id, { shouldValidate: true });
      clearErrors("isoAgentEmail");
    } else {
      setValue("isoAgentEmail", "", { shouldValidate: true });
      setValue("isoAgentId", null, { shouldValidate: true });
    }
  };

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    isoAgentEmail: {
      fieldType: "textInput",
      fieldName: "isoAgentEmail",
      label: t(`${FORM_LABEL_KEY}.isoAgentEmail`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        rules: [
          "validEmail",
        ]
      },
    },
  };

  return (
    <FormBuilder.Section
      titleVariant="h3"
      padding="0 24px"
    >
      <Box sx={{ mb: 2 }}> 
        <RadioGroup row value={mode} onChange={handleModeChange}>
          <FormControlLabel value="existing" control={<Radio />} label={t(`${FORM_LABEL_KEY}.assignExistingAgent`)} />
          <FormControlLabel value="new" control={<Radio />} label={t(`${FORM_LABEL_KEY}.createNewAgent`)} />
        </RadioGroup>
      </Box>

      <FormBuilder.Row itemsPerRow={2}>
        {mode === "existing" ? (
          <Box sx={{ flexGrow: 1, flexBasis: "300px", mb: "22px" }}>
            <AsyncAutocomplete
              label={t(`${FORM_LABEL_KEY}.agent`)}
              required
              fetchOptions={onSearch} // onSearch returns Promise<Array>
              preloadedOptions={agentList}
              useLocalFilter
              hasMoreOptions={hasMoreAgents}
              value={selectedAgent}
              onChange={handleAgentChange}
              getOptionLabel={(option) => option.title || option.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              placeholder={t(`${FORM_LABEL_KEY}.agentSearchPlaceholder`)}
              disabled={isDisabled}
              error={!!agentError}
              errorMessage={agentError?.message}
            />
          </Box>
        ) : (
          FormBuilder.buildFields({
            formProps,
            fields: [{
              ...fieldsConfig.isoAgentEmail,
              config: {
                ...fieldsConfig.isoAgentEmail.config,
                placeholder: t(`${FORM_LABEL_KEY}.newAgentEmail`),
              }
            }],
          })
        )}
      </FormBuilder.Row>
    </FormBuilder.Section>
  );
};

export default React.memo(AgentAssignmentSection);
